import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { Mensagem } from '../../core/api/mensagem.dto';
import { MensagemService } from '../../core/api/mensagem.service';
import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';
import { Ordenacao, OrdenavelComponent, paraParametroSort } from '../../shared/ordenavel/ordenavel.component';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import { MensagemDetalheDialogComponent } from './mensagem-detalhe/mensagem-detalhe.dialog';

/**
 * Registro do que o sistema mandou para o serviço de e-mail.
 *
 * Pontos de chamada: `GET /mensagens`. O detalhe, a edição de parâmetros e o reenvio ficam no
 * diálogo, porque os três são o mesmo ciclo de diagnóstico: abrir, trocar o remetente, reenviar
 * para si mesmo e ler o que o serviço respondeu.
 */
@Component({
  selector: 'app-mensagens',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbFormFieldModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    ListaVaziaComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './mensagens.component.html',
  styleUrl: './mensagens.component.scss',
})
export class MensagensComponent implements OnInit {
  mensagens: Mensagem[] = [];
  page = 1;
  size = 10;
  totalPages = 0;
  carregando = false;
  filtro = '';

  /** `TAB-01` — ordenação no servidor. O padrão é o do contrato: mais recentes primeiro. */
  ordenacao: Ordenacao | null = { campo: 'criadoEm', direcao: 'desc' };

  constructor(
    private service: MensagemService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  protected get sort(): string | null {
    return paraParametroSort(this.ordenacao);
  }

  get temFiltro(): boolean {
    return this.filtro.trim() !== '';
  }

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
    this.page = 1;
    this.carregar();
  }

  /**
   * `FIL-01` — Enter ou botão. O filtro é trecho, sem caixa e sem acento, do e-mail ou do nome do
   * catequizando, da mãe ou do pai da pré-inscrição de origem — a busca é do servidor.
   */
  buscarAgora(): void {
    this.page = 1;
    this.carregar();
  }

  limparFiltro(): void {
    this.filtro = '';
    this.buscarAgora();
  }

  onPageChange(p: number): void {
    if (p < 1 || (this.totalPages && p > this.totalPages)) return;
    this.page = p;
    this.carregar();
  }

  carregar(): void {
    this.carregando = true;
    this.service
      .listar(this.page - 1, this.size, this.filtro, this.sort)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (pagina) => {
          this.mensagens = pagina.content ?? [];
          this.totalPages = pagina.totalPages ?? 0;
        },
        error: (err) => {
          this.mensagens = [];
          this.totalPages = 0;
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar as mensagens.'), 'Erro');
        },
      });
  }

  abrir(m: Mensagem): void {
    if (!m.id) return;
    this.dialogService
      .open(MensagemDetalheDialogComponent, { context: { mensagemId: m.id } })
      .onClose.subscribe((mudou) => {
        if (mudou) this.carregar();
      });
  }
}
